/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';

/**
 * Login Google na WEB via Google Identity Services (GIS), fluxo de ID token.
 *
 * Substitui o gapi.auth2 legado que o plugin @southdevs/capacitor-google-auth usa na web
 * e que o Google desligou (quebra com o fim dos cookies de terceiros / FedCM — visível nos
 * 400 do iframe o/oauth2/iframe). Retorna o ID token (JWT), que é exatamente o
 * `googleIdToken` que o backend valida — o mesmo credential que o SDK nativo produz, com
 * `aud` = client id web do app.
 *
 * Técnica: renderiza o botão OFICIAL do GIS num container escondido e o aciona
 * programaticamente no clique do botão custom do app. O acionamento precisa ocorrer dentro
 * do gesto do usuário, por isso o script e a inicialização são pré-carregados (preload) na
 * entrada da tela — assim, no clique, não há await de rede que invalide o gesto.
 *
 * Requisito de console (igual ao gapi): o client web precisa ser do tipo "Web application"
 * e ter a origem (ex.: https://app.veggly.net) em Authorized JavaScript origins.
 */
@Injectable({ providedIn: 'root' })
export class GoogleWebAuthService {
  private scriptPromise?: Promise<void>;
  private initializedClientId?: string;
  private hiddenContainer?: HTMLElement;
  private pending?: { resolve: (idToken: string) => void; reject: (err: any) => void };

  /** Carrega o script do GIS uma única vez. */
  private loadScript(): Promise<void> {
    if (this.scriptPromise) {
      return this.scriptPromise;
    }
    this.scriptPromise = new Promise<void>((resolve, reject) => {
      if ((window as any).google?.accounts?.id) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Falha ao carregar o Google Identity Services'));
      document.head.appendChild(script);
    });
    return this.scriptPromise;
  }

  /**
   * Pré-carrega e inicializa o GIS + renderiza o botão escondido.
   * Chame na entrada da tela de login (web) para que o clique fique síncrono.
   */
  async preload(clientId: string): Promise<void> {
    await this.loadScript();
    if (this.initializedClientId === clientId) {
      return;
    }
    const google = (window as any).google;
    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: any) => {
        const pending = this.pending;
        this.pending = undefined;
        if (!pending) {
          return;
        }
        if (response?.credential) {
          pending.resolve(response.credential);
        } else {
          pending.reject(new Error('Login Google sem credential'));
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      use_fedcm_for_prompt: true,
    });

    if (!this.hiddenContainer) {
      this.hiddenContainer = document.createElement('div');
      this.hiddenContainer.setAttribute('aria-hidden', 'true');
      this.hiddenContainer.style.position = 'fixed';
      this.hiddenContainer.style.top = '0';
      this.hiddenContainer.style.left = '0';
      this.hiddenContainer.style.width = '1px';
      this.hiddenContainer.style.height = '1px';
      this.hiddenContainer.style.overflow = 'hidden';
      this.hiddenContainer.style.opacity = '0';
      this.hiddenContainer.style.pointerEvents = 'none';
      this.hiddenContainer.style.zIndex = '-1';
      document.body.appendChild(this.hiddenContainer);
    }
    this.hiddenContainer.innerHTML = '';
    google.accounts.id.renderButton(this.hiddenContainer, { type: 'standard', text: 'signin_with' });

    this.initializedClientId = clientId;
  }

  /**
   * Dispara o login e resolve com o ID token (JWT).
   * Deve ser chamado dentro do handler de clique; requer preload() prévio para não perder
   * o gesto do usuário. Se o botão ainda não estiver renderizado, recorre ao One Tap/FedCM.
   */
  async signInGetIdToken(clientId: string): Promise<string> {
    if (this.initializedClientId !== clientId) {
      // Cold start (sem preload): pode perder o gesto do usuário no await de rede.
      await this.preload(clientId);
    }
    return new Promise<string>((resolve, reject) => {
      if (this.pending) {
        this.pending.reject(new Error('Login Google substituído por nova tentativa'));
      }
      this.pending = { resolve, reject };

      const button = this.hiddenContainer?.querySelector('div[role="button"]') as HTMLElement | null;
      if (button) {
        button.click();
      } else {
        // Fallback: One Tap / FedCM (menos confiável, mas não depende do botão renderizado).
        (window as any).google?.accounts?.id?.prompt();
      }
    });
  }

  /** Decodifica o payload do JWT (ID token) para obter email/nome/sub no cliente. */
  decodeIdToken(idToken: string): any {
    try {
      const payload = idToken.split('.')[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        Array.prototype.map
          .call(atob(normalized), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
}
